import React, { useState } from 'react';
import { 
  Calendar, Clock, BookOpen, Users, MapPin, Award, 
  Printer, Download, ShieldCheck, CheckCircle2, ChevronRight, 
  Sparkles, Video, Coffee, MessageSquare, AlertCircle, Building2,
  FileText, Search, Filter, ArrowRight, Laptop, Layers
} from 'lucide-react';
import { INSTITUTION_INFO } from '../data/portalData';

export const ClassTimetable = ({ setActiveView }) => {
  const [selectedCohortTab, setSelectedCohortTab] = useState('ALL');
  const [activeDayFilter, setActiveDayFilter] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('cohort'); // 'cohort' | 'matrix'

  const handlePrint = () => {
    window.print();
  };

  // Cohort Schedule Data
  const cohortSchedules = [
    {
      id: 'MBA',
      title: "Master of Business Administration (MBA)",
      subtitle: "Pioneer Executive Cohort · Weekday Evening Cadence",
      totalCredits: 15,
      contactHours: "15 hrs / week",
      venue: "AKSU Liaison Office, Uyo (Hall A)",
      timing: "Monday – Friday · 6:00 pm – 9:00 pm",
      badge: "Master's Cadence",
      badgeColor: "bg-blue-900 text-amber-300 border-amber-400/40",
      description: "Evening executive lectures tailored for managers, entrepreneurs, and corporate leaders balancing professional responsibilities.",
      courses: [
        {
          day: 'Monday',
          time: '6:00 – 9:00 pm',
          code: 'BUA 801',
          title: 'Managerial Accounting',
          lecturer: 'Dr Uwem E. Uwah, FCA',
          cu: 3,
          hall: 'Hall A',
          mode: 'Hybrid (In-Person & Live Virtual)'
        },
        {
          day: 'Tuesday',
          time: '6:00 – 9:00 pm',
          code: 'BUA 811',
          title: 'Managerial Economics',
          lecturer: 'Dr Ekere Daniel',
          cu: 3,
          hall: 'Hall A',
          mode: 'Hybrid (In-Person & Live Virtual)'
        },
        {
          day: 'Wednesday',
          time: '6:00 – 9:00 pm',
          code: 'BUA 821',
          title: 'Marketing Management & Consumer Behaviour',
          lecturer: 'Prof Nkanikpo Ibok',
          cu: 3,
          hall: 'Hall A',
          mode: 'Hybrid (In-Person & Live Virtual)'
        },
        {
          day: 'Thursday',
          time: '6:00 – 9:00 pm',
          code: 'BUA 831',
          title: 'Organisational Behaviour & Leadership',
          lecturer: 'Prof Michael Namseh',
          cu: 3,
          hall: 'Hall A',
          mode: 'Hybrid (In-Person & Live Virtual)'
        },
        {
          day: 'Friday',
          time: '6:00 – 9:00 pm',
          code: 'BUA 841',
          title: 'Principles of Management & Business Communication',
          lecturer: 'Prof Christabel Brownson',
          cu: 3,
          hall: 'Hall A',
          mode: 'Hybrid (In-Person & Live Virtual)'
        }
      ]
    },
    {
      id: 'MAF',
      title: "Master of Accounting and Finance (MAF)",
      subtitle: "Pioneer Executive Cohort · Weekday Evening Cadence",
      totalCredits: 14,
      contactHours: "14 hrs / week",
      venue: "AKSU Liaison Office, Uyo (Hall B)",
      timing: "Monday – Friday · 6:00 pm – 9:00 pm (Wed: 6:00 – 8:00 pm)",
      badge: "Master's Cadence",
      badgeColor: "bg-emerald-950 text-emerald-300 border-emerald-400/40",
      description: "Rigorous evening advanced financial curriculum aligned with international reporting benchmarks and treasury leadership.",
      courses: [
        {
          day: 'Monday',
          time: '6:00 – 9:00 pm',
          code: 'ACC 811',
          title: 'Management Accounting & Control',
          lecturer: 'Dr Sunday Okpo, FCA',
          cu: 3,
          hall: 'Hall B',
          mode: 'Hybrid (In-Person & Live Virtual)'
        },
        {
          day: 'Tuesday',
          time: '6:00 – 9:00 pm',
          code: 'ACC 801',
          title: 'Financial Reporting under IFRS',
          lecturer: 'Dr Mbobo Mbobo, FCA',
          cu: 3,
          hall: 'Hall B',
          mode: 'Hybrid (In-Person & Live Virtual)'
        },
        {
          day: 'Wednesday',
          time: '6:00 – 8:00 pm',
          code: 'MAF 811',
          title: 'Research Methodology',
          lecturer: 'Dr Udeme Eshiet',
          cu: 2,
          hall: 'Hall B',
          mode: 'Hybrid (In-Person & Live Virtual)'
        },
        {
          day: 'Thursday',
          time: '6:00 – 9:00 pm',
          code: 'FIN 811',
          title: 'Corporate Finance and Valuation',
          lecturer: 'Prof Anthonia Ubom',
          cu: 3,
          hall: 'Hall B',
          mode: 'Hybrid (In-Person & Live Virtual)'
        },
        {
          day: 'Friday',
          time: '6:00 – 9:00 pm',
          code: 'MAF 801',
          title: 'Business Statistics and Data Analytics',
          lecturer: 'Dr Imoh Uford',
          cu: 3,
          hall: 'Hall B',
          mode: 'Hybrid (In-Person & Live Virtual)'
        }
      ]
    },
    {
      id: 'DBA',
      title: "Doctor of Business Administration (DBA)",
      subtitle: "Pioneer Doctoral Cohort · Friday Afternoon & Saturday Intensive Cadence",
      totalCredits: 13,
      contactHours: "13 hrs / week",
      venue: "AKSU Liaison Office, Uyo (Hall A)",
      timing: "Friday: 2:00 pm – 5:00 pm · Saturday: 8:00 am – 7:00 pm",
      badge: "Doctoral Cadence",
      badgeColor: "bg-amber-950 text-amber-200 border-amber-400/40",
      description: "Senior executive doctoral research rhythm designed for permanent secretaries, CEOs, and C-suite directors.",
      courses: [
        {
          day: 'Friday',
          time: '2:00 – 5:00 pm',
          code: 'BUS 931',
          title: 'Competitive Strategy',
          lecturer: 'Prof Anietie Akpan',
          cu: 3,
          hall: 'Hall A',
          mode: 'Hybrid (In-Person & Live Virtual)'
        },
        {
          day: 'Saturday',
          time: '8:00 – 11:00 am',
          code: 'BUS 911',
          title: 'Advanced Quantitative Analysis & Econometrics',
          lecturer: 'Dr Ekere Daniel',
          cu: 3,
          hall: 'Hall A',
          mode: 'Hybrid (In-Person & Live Virtual)'
        },
        {
          day: 'Saturday',
          time: '11:00 am – 1:00 pm',
          code: 'BUS 901',
          title: 'Data Science and Artificial Intelligence',
          lecturer: 'Dr Imoh Uford',
          cu: 2,
          hall: 'Hall A',
          mode: 'Hybrid (In-Person & Live Virtual)'
        },
        {
          day: 'Saturday',
          time: '1:00 – 2:00 pm',
          code: 'RECESS',
          title: 'Executive Lunch Break & Networking (Refreshments Provided)',
          lecturer: 'Directorate Courtesy',
          cu: 0,
          hall: 'Executive Lounge',
          mode: 'In-Person Lunch'
        },
        {
          day: 'Saturday',
          time: '2:00 – 5:00 pm',
          code: 'BUS 921',
          title: 'Advanced Business Management Theory',
          lecturer: 'Prof Christabel Brownson',
          cu: 3,
          hall: 'Hall A',
          mode: 'Hybrid (In-Person & Live Virtual)'
        },
        {
          day: 'Saturday',
          time: '5:00 – 7:00 pm',
          code: 'BUS 941',
          title: 'Business Writing, Publication and Research',
          lecturer: 'Dr Eno Ukpong (Assoc. Prof.)',
          cu: 2,
          hall: 'Hall A',
          mode: 'Hybrid (In-Person & Live Virtual)'
        }
      ]
    },
    {
      id: 'DAF',
      title: "Doctor of Accounting and Finance (DAF)",
      subtitle: "Pioneer Doctoral Cohort · Friday Afternoon & Saturday Intensive Cadence",
      totalCredits: 13,
      contactHours: "13 hrs / week",
      venue: "AKSU Liaison Office, Uyo (Hall B)",
      timing: "Friday: 1:00 pm – 6:00 pm · Saturday: 8:00 am – 5:00 pm",
      badge: "Doctoral Cadence",
      badgeColor: "bg-slate-900 text-amber-300 border-amber-400/40",
      description: "Terminal academic and professional financial doctorate focusing on asset pricing, financial econometric theory, and epistemology.",
      courses: [
        {
          day: 'Friday',
          time: '1:00 – 3:00 pm',
          code: 'DAF 921',
          title: 'Business Writing, Publication and Research',
          lecturer: 'Dr Eno Ukpong (Assoc. Prof.)',
          cu: 2,
          hall: 'Hall B',
          mode: 'Hybrid (In-Person & Live Virtual)'
        },
        {
          day: 'Friday',
          time: '3:00 – 6:00 pm',
          code: 'FIN 911',
          title: 'Theory of Finance and Asset Pricing',
          lecturer: 'Prof Anthonia Ubom',
          cu: 3,
          hall: 'Hall B',
          mode: 'Hybrid (In-Person & Live Virtual)'
        },
        {
          day: 'Saturday',
          time: '8:00 – 11:00 am',
          code: 'ACC 911',
          title: 'Advanced Financial Accounting Theory & Research',
          lecturer: 'Prof Joseph Udoayang',
          cu: 3,
          hall: 'Hall B',
          mode: 'Hybrid (In-Person & Live Virtual)'
        },
        {
          day: 'Saturday',
          time: '11:00 am – 1:00 pm',
          code: 'DAF 901',
          title: 'Philosophy and Foundations of Business Research',
          lecturer: 'Prof Nkanikpo Ibok',
          cu: 2,
          hall: 'Hall B',
          mode: 'Hybrid (In-Person & Live Virtual)'
        },
        {
          day: 'Saturday',
          time: '1:00 – 2:00 pm',
          code: 'RECESS',
          title: 'Executive Lunch Break & Networking (Refreshments Provided)',
          lecturer: 'Directorate Courtesy',
          cu: 0,
          hall: 'Executive Lounge',
          mode: 'In-Person Lunch'
        },
        {
          day: 'Saturday',
          time: '2:00 – 5:00 pm',
          code: 'DAF 911',
          title: 'Advanced Quantitative Analysis & Econometrics',
          lecturer: 'Dr Ekere Daniel',
          cu: 3,
          hall: 'Hall B',
          mode: 'Hybrid (In-Person & Live Virtual)'
        }
      ]
    }
  ];

  // Days list for filter
  const days = ['ALL', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  // Flatten all courses for master search & matrix
  const allCoursesFlat = cohortSchedules.flatMap(cohort => 
    cohort.courses.map(course => ({
      ...course,
      cohortId: cohort.id,
      cohortTitle: cohort.title
    }))
  );

  const filteredCohorts = cohortSchedules
    .filter(cohort => selectedCohortTab === 'ALL' || cohort.id === selectedCohortTab)
    .map(cohort => {
      const courses = cohort.courses.filter(course => {
        const matchesDay = activeDayFilter === 'ALL' || course.day === activeDayFilter;
        const matchesSearch = searchQuery === '' || 
          course.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
          course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          course.lecturer.toLowerCase().includes(searchQuery.toLowerCase()) ||
          course.day.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesDay && matchesSearch;
      });
      return { ...cohort, filteredCourses: courses };
    });

  return (
    <div className="min-h-screen bg-[#FBF9F5] text-slate-900 py-10 px-4 sm:px-6 lg:px-8 print:py-0 print:px-0 print:bg-white">
      <div className="max-w-7xl mx-auto space-y-8 print:space-y-4">
        
        {/* ========================================================= */}
        {/* TOP ACTION TOOLBAR (Hidden on Print) */}
        {/* ========================================================= */}
        <div className="print:hidden bg-gradient-to-r from-[#030F1C] via-[#0B2644] to-[#7A470E] rounded-3xl p-5 sm:p-6 text-white shadow-2xl border border-amber-500/30 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center space-x-3.5">
            <div className="w-12 h-12 rounded-2xl bg-amber-500/20 border border-amber-400/40 flex items-center justify-center text-amber-300">
              <Calendar className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <span className="text-[11px] font-mono font-bold uppercase tracking-wider text-amber-300 bg-amber-950/60 px-2 py-0.5 rounded border border-amber-500/30">
                  Official Document
                </span>
                <span className="text-xs text-slate-300 font-mono">Ref: AKSU/BS/TT/2025-2026/01</span>
              </div>
              <h1 className="text-lg sm:text-2xl font-black text-white font-sc tracking-wide mt-0.5">
                Executive Lecture Timetable · 2025/2026
              </h1>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center bg-white/10 rounded-xl p-1 border border-white/15">
              <button
                onClick={() => setViewMode('cohort')}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all font-sc ${
                  viewMode === 'cohort' ? 'bg-[#7A470E] text-white shadow' : 'text-slate-300 hover:text-white'
                }`}
              >
                By Cohort
              </button>
              <button
                onClick={() => setViewMode('matrix')}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all font-sc ${
                  viewMode === 'matrix' ? 'bg-[#7A470E] text-white shadow' : 'text-slate-300 hover:text-white'
                }`}
              >
                Weekly Matrix
              </button>
            </div>

            <button
              onClick={handlePrint}
              className="px-4 py-2 bg-white text-[#0B2644] hover:bg-amber-100 font-black text-xs rounded-xl shadow-md transition-all flex items-center space-x-2 cursor-pointer font-sc"
            >
              <Printer className="w-4 h-4 text-[#7A470E]" />
              <span>Print Timetable (A4)</span>
            </button>
          </div>
        </div>

        {/* ========================================================= */}
        {/* OFFICIAL LETTERHEAD & MASTHEAD CONTAINER */}
        {/* ========================================================= */}
        <div className="bg-white rounded-3xl border-2 border-slate-200/80 shadow-xl p-6 sm:p-10 relative overflow-hidden print:border-none print:shadow-none print:p-2 print:rounded-none">
          
          {/* Subtle Watermark */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.03] print:opacity-[0.02]">
            <img src="/Logo.png" alt="AKSU Crest" className="w-[550px] h-[550px] object-contain" />
          </div>

          {/* Institutional Header Banner */}
          <div className="border-b-4 border-[#7A470E] pb-6 mb-8 print:pb-4 print:mb-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
              
              {/* Crest & Title Group */}
              <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 sm:space-x-4">
                <div className="w-20 h-20 sm:w-24 sm:h-24 bg-white rounded-2xl p-2 border border-slate-200 shadow-sm flex items-center justify-center shrink-0">
                  <img src="/Logo.png" alt="AKSU Crest" className="w-full h-full object-contain" />
                </div>
                <div>
                  <span className="text-xs uppercase font-extrabold tracking-[0.2em] text-[#7A470E] font-sc block">
                    Akwa Ibom State University
                  </span>
                  <h1 className="text-2xl sm:text-4xl font-black text-[#0B2644] font-sc tracking-wide leading-tight">
                    BUSINESS SCHOOL
                  </h1>
                  <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#7A470E] font-sc block mt-0.5">
                    Knowledge · Enterprise · Leadership
                  </span>
                  <p className="text-[11px] text-slate-500 mt-1 font-sans">
                    Obio Akpa Campus, Oruk Anam LGA · Liaison Office: 6 Tony Asikpo Street, Shelter Afrique, Uyo
                  </p>
                </div>
              </div>

              {/* Document Stamp Box */}
              <div className="bg-amber-50/70 border-2 border-amber-300 rounded-2xl p-3 sm:p-4 text-center sm:text-right shrink-0">
                <div className="text-[10px] font-mono font-bold uppercase tracking-widest text-[#7A470E]">
                  Academic Session
                </div>
                <div className="text-base sm:text-lg font-black text-[#0B2644] font-sc">
                  2025 / 2026 Session
                </div>
                <div className="text-[11px] font-bold text-emerald-800 font-mono">
                  Pioneer Cohort · 1st Semester
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5 font-mono">
                  15 Weeks (26 Oct 2026 – 6 Feb 2027)
                </div>
              </div>

            </div>
          </div>

          {/* Timetable Sub-Header Banner */}
          <div className="text-center max-w-4xl mx-auto mb-8">
            <div className="inline-flex items-center space-x-2 text-xs font-black uppercase tracking-widest text-[#0B2644] bg-amber-100/70 px-3.5 py-1 rounded-full border border-amber-300 font-sc mb-3">
              <Sparkles className="w-3.5 h-3.5 text-[#7A470E]" />
              <span>Hybrid Executive Delivery Framework</span>
            </div>
            <h2 className="text-2xl sm:text-4xl font-black text-[#0B2644] font-sc uppercase tracking-tight">
              First-Semester Lecture Timetable
            </h2>
            <p className="mt-2 text-xs sm:text-sm text-slate-600 font-medium max-w-2xl mx-auto leading-relaxed">
              Operating from the <strong>AKSU Liaison Office, 6 Tony Asikpo Street, Shelter Afrique, Uyo</strong> in parallel session halls (Hall A & Hall B) with concurrent live virtual interactive streaming.
            </p>
          </div>

          {/* ========================================================= */}
          {/* SEMESTER DESIGN & KEY DELIVERY METRICS STRIP */}
          {/* ========================================================= */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            
            {/* Metric 1 */}
            <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 shadow-2xs">
              <div className="flex items-center space-x-2 text-[#7A470E] mb-1">
                <Users className="w-4 h-4" />
                <span className="text-[11px] font-bold uppercase tracking-wider font-sc">Pioneer Cohort</span>
              </div>
              <div className="text-lg font-black text-[#0B2644] font-sc">6 Scholar-Practitioners</div>
              <p className="text-[11px] text-slate-500 mt-0.5">3 in DBA · 2 in DAF · 1 in MAF & MBA</p>
            </div>

            {/* Metric 2 */}
            <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 shadow-2xs">
              <div className="flex items-center space-x-2 text-[#7A470E] mb-1">
                <Building2 className="w-4 h-4" />
                <span className="text-[11px] font-bold uppercase tracking-wider font-sc">Dual Lecture Halls</span>
              </div>
              <div className="text-lg font-black text-[#0B2644] font-sc">Hall A & Hall B</div>
              <p className="text-[11px] text-slate-500 mt-0.5">Parallel non-displacing sessions · Zero conflict</p>
            </div>

            {/* Metric 3 */}
            <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 shadow-2xs">
              <div className="flex items-center space-x-2 text-[#7A470E] mb-1">
                <Clock className="w-4 h-4" />
                <span className="text-[11px] font-bold uppercase tracking-wider font-sc">15 Lecture Weeks</span>
              </div>
              <div className="text-lg font-black text-[#0B2644] font-sc">26 Oct 2026 – 6 Feb 2027</div>
              <p className="text-[11px] text-slate-500 mt-0.5">Plus 2 wks Revision & 3 wks Examinations</p>
            </div>

            {/* Metric 4 */}
            <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 shadow-2xs">
              <div className="flex items-center space-x-2 text-[#7A470E] mb-1">
                <Laptop className="w-4 h-4" />
                <span className="text-[11px] font-bold uppercase tracking-wider font-sc">Executive Mode</span>
              </div>
              <div className="text-lg font-black text-[#0B2644] font-sc">Hybrid Stream Active</div>
              <p className="text-[11px] text-slate-500 mt-0.5">Weekday Evenings & Weekend Seminars</p>
            </div>

          </div>

          {/* ========================================================= */}
          {/* INTERACTIVE CONTROLS (Filtered by Cohort & Day) */}
          {/* ========================================================= */}
          <div className="print:hidden bg-slate-100/80 rounded-2xl p-4 mb-8 space-y-3 border border-slate-200">
            <div className="flex flex-wrap items-center justify-between gap-3">
              
              {/* Cohort Tabs */}
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-xs font-bold text-slate-600 font-sc mr-1">Filter Cohort:</span>
                {[
                  { id: 'ALL', label: 'All 4 Cohorts' },
                  { id: 'MBA', label: 'MBA (15 CU)' },
                  { id: 'MAF', label: 'MAF (14 CU)' },
                  { id: 'DBA', label: 'DBA (13 CU)' },
                  { id: 'DAF', label: 'DAF (13 CU)' }
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setSelectedCohortTab(tab.id)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold font-sc transition-all cursor-pointer ${
                      selectedCohortTab === tab.id
                        ? 'bg-[#0B2644] text-white shadow-md border border-[#7A470E]'
                        : 'bg-white text-slate-700 hover:bg-amber-50 border border-slate-200'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Search Bar */}
              <div className="relative w-full sm:w-64">
                <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search code, title, or lecturer..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-1.5 text-xs bg-white rounded-xl border border-slate-300 focus:outline-none focus:border-[#7A470E]"
                />
              </div>

            </div>

            {/* Day Pills Filter */}
            <div className="flex flex-wrap items-center gap-1.5 pt-2 border-t border-slate-200/80">
              <span className="text-xs font-bold text-slate-600 font-sc mr-1 flex items-center">
                <Filter className="w-3.5 h-3.5 mr-1 text-[#7A470E]" />
                <span>Day Filter:</span>
              </span>
              {days.map(d => (
                <button
                  key={d}
                  onClick={() => setActiveDayFilter(d)}
                  className={`px-2.5 py-1 rounded-lg text-[11px] font-bold font-sc transition-all cursor-pointer ${
                    activeDayFilter === d
                      ? 'bg-[#7A470E] text-white shadow-xs'
                      : 'bg-white text-slate-600 hover:bg-slate-200/80 border border-slate-200'
                  }`}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>

          {/* ========================================================= */}
          {/* COHORT SCHEDULE SECTIONS */}
          {/* ========================================================= */}
          {viewMode === 'cohort' ? (
            <div className="space-y-10">
              {filteredCohorts.map((cohort) => (
                <div 
                  key={cohort.id} 
                  className="rounded-3xl border-2 border-slate-200 shadow-md overflow-hidden bg-white print:border print:shadow-none print:rounded-xl"
                >
                  {/* Cohort Header Bar */}
                  <div className="bg-gradient-to-r from-[#030F1C] via-[#0B2644] to-[#12365c] text-white p-5 sm:p-6 flex flex-wrap items-center justify-between gap-4 border-b-2 border-[#7A470E]">
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className={`text-[10px] font-mono font-bold uppercase tracking-widest px-2.5 py-0.5 rounded-full border ${cohort.badgeColor}`}>
                          {cohort.badge}
                        </span>
                        <span className="text-xs text-amber-300 font-mono font-bold">
                          {cohort.contactHours}
                        </span>
                      </div>
                      <h3 className="text-xl sm:text-2xl font-black text-white font-sc tracking-wide mt-1">
                        {cohort.title}
                      </h3>
                      <p className="text-xs text-slate-300 mt-0.5 font-medium">
                        {cohort.subtitle} · Venue: <strong className="text-amber-300">{cohort.venue}</strong>
                      </p>
                    </div>

                    <div className="bg-white/10 rounded-2xl p-3 border border-white/20 text-center sm:text-right">
                      <div className="text-[10px] uppercase font-mono tracking-wider text-slate-300">Total Credit Load</div>
                      <div className="text-2xl font-black text-amber-300 font-sc">{cohort.totalCredits} CU / Week</div>
                    </div>
                  </div>

                  {/* Table of Lectures */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-amber-50/70 border-b border-amber-200 text-[#0B2644] text-xs font-black uppercase tracking-wider font-sc">
                          <th className="py-3.5 px-4 sm:px-6 w-32">Day</th>
                          <th className="py-3.5 px-4 w-40">Time</th>
                          <th className="py-3.5 px-4 w-28">Code</th>
                          <th className="py-3.5 px-4">Course Title</th>
                          <th className="py-3.5 px-4">Assigned Lecturer</th>
                          <th className="py-3.5 px-4 text-center w-20">CU</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 text-xs sm:text-sm text-slate-800 font-sans">
                        {cohort.filteredCourses.length > 0 ? (
                          cohort.filteredCourses.map((c, idx) => {
                            const isRecess = c.code === 'RECESS';
                            return (
                              <tr 
                                key={idx} 
                                className={`transition-colors ${
                                  isRecess 
                                    ? 'bg-amber-50/80 italic text-amber-900 font-medium' 
                                    : 'hover:bg-amber-50/30'
                                }`}
                              >
                                <td className="py-3.5 px-4 sm:px-6 font-bold text-[#0B2644] font-sc">
                                  {c.day}
                                </td>
                                <td className="py-3.5 px-4 font-mono font-semibold text-slate-700 whitespace-nowrap">
                                  {c.time}
                                </td>
                                <td className="py-3.5 px-4">
                                  {isRecess ? (
                                    <span className="px-2 py-0.5 bg-amber-200 text-amber-900 rounded font-mono text-[10px] font-bold">
                                      RECESS
                                    </span>
                                  ) : (
                                    <span className="px-2 py-1 bg-slate-100 text-[#0B2644] rounded-md font-mono font-bold text-xs border border-slate-200">
                                      {c.code}
                                    </span>
                                  )}
                                </td>
                                <td className="py-3.5 px-4 font-semibold text-slate-900">
                                  <div>{c.title}</div>
                                  <div className="text-[10px] text-slate-500 font-normal mt-0.5">
                                    {c.hall} · {c.mode}
                                  </div>
                                </td>
                                <td className="py-3.5 px-4 text-[#7A470E] font-medium font-sc">
                                  {c.lecturer}
                                </td>
                                <td className="py-3.5 px-4 text-center font-bold text-slate-900 font-mono">
                                  {c.cu > 0 ? c.cu : '—'}
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan={6} className="py-8 text-center text-slate-500 italic">
                              No lecture sessions matched the active filter or search query.
                            </td>
                          </tr>
                        )}
                        {/* Weekly Total Footer Row */}
                        <tr className="bg-slate-50 font-black text-xs sm:text-sm text-[#0B2644] border-t-2 border-slate-200 font-sc">
                          <td colSpan={5} className="py-3.5 px-4 sm:px-6 text-right uppercase tracking-wider">
                            Total Credit Units per Week:
                          </td>
                          <td className="py-3.5 px-4 text-center font-mono text-base font-black text-[#7A470E]">
                            {cohort.totalCredits} CU
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* ========================================================= */
            /* WEEKLY MASTER MATRIX VIEW */
            /* ========================================================= */
            <div className="space-y-6">
              {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((dayName) => {
                const daySessions = allCoursesFlat.filter(c => c.day === dayName);
                if (daySessions.length === 0) return null;

                return (
                  <div key={dayName} className="rounded-2xl border-2 border-slate-200 overflow-hidden bg-white shadow-sm">
                    <div className="bg-[#0B2644] text-white py-2.5 px-5 flex items-center justify-between">
                      <span className="font-black text-base font-sc uppercase tracking-wider text-amber-300">
                        {dayName}
                      </span>
                      <span className="text-xs text-slate-300 font-mono">
                        {daySessions.length} Scheduled Sessions
                      </span>
                    </div>

                    <div className="divide-y divide-slate-100">
                      {daySessions.map((session, sIdx) => (
                        <div key={sIdx} className="p-4 sm:p-5 flex flex-wrap items-center justify-between gap-4 hover:bg-slate-50">
                          <div className="space-y-1">
                            <div className="flex items-center space-x-2">
                              <span className="px-2 py-0.5 rounded text-[10px] font-bold font-mono bg-[#0B2644] text-white">
                                {session.cohortId}
                              </span>
                              <span className="px-2 py-0.5 rounded text-[10px] font-bold font-mono bg-amber-100 text-[#7A470E] border border-amber-300">
                                {session.code}
                              </span>
                              <span className="text-xs font-mono font-bold text-slate-500">
                                {session.time}
                              </span>
                            </div>
                            <h4 className="text-sm sm:text-base font-bold text-slate-900 font-sc">
                              {session.title}
                            </h4>
                            <p className="text-xs text-slate-600">
                              Lecturer: <strong className="text-[#7A470E]">{session.lecturer}</strong> · Venue: <span className="text-slate-800 font-medium">{session.hall}</span>
                            </p>
                          </div>

                          <div className="flex items-center space-x-3">
                            <span className="text-xs font-mono font-bold bg-slate-100 text-[#0B2644] px-2.5 py-1 rounded-lg border border-slate-200">
                              {session.cu > 0 ? `${session.cu} Credit Units` : 'Break'}
                            </span>
                            <span className="text-[11px] text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200 font-mono font-bold">
                              Hybrid Stream
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ========================================================= */}
          {/* STATUTORY REGULATORY & OPERATIONAL DELIVERY NOTES */}
          {/* ========================================================= */}
          <div className="mt-12 rounded-3xl bg-amber-50/60 border-2 border-amber-300/80 p-6 sm:p-8 space-y-5 print:rounded-xl print:p-4 print:mt-6">
            
            <div className="flex items-center space-x-2.5 border-b border-amber-300/80 pb-3">
              <ShieldCheck className="w-5 h-5 text-[#7A470E]" />
              <h3 className="text-base sm:text-lg font-black uppercase tracking-wider text-[#0B2644] font-sc">
                Statutory Delivery & Academic Regulations Notes
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-xs sm:text-sm text-slate-700 leading-relaxed font-sans">
              
              <div className="space-y-3.5">
                <div>
                  <strong className="text-[#0B2644] font-sc block">1. Weekly Contact Hours:</strong>
                  <span>MBA 15 CU (15 hrs/wk); MAF 14 CU (14 hrs/wk); DBA 13 CU (13 hrs/wk); DAF 13 CU (13 hrs/wk). Each credit unit corresponds directly to one lecture-hour per week over the 15-week academic semester.</span>
                </div>

                <div>
                  <strong className="text-[#0B2644] font-sc block">2. Delivery Mode & Attendance Mandate:</strong>
                  <span>Face-to-face instruction at the Liaison Office (6 Tony Asikpo Street, Shelter Afrique, Uyo) is the default. The School streams each lecture concurrently via its virtual-classroom platform for candidates unable to attend in person; attendance in either mode counts toward the statutory <strong>75% attendance requirement</strong> for sitting the end-of-semester examination.</span>
                </div>

                <div>
                  <strong className="text-[#0B2644] font-sc block">3. Pioneer Cohort Virtual Mode Flexibility:</strong>
                  <span>At the moment, every student in Cohort 1 prefers the virtual mode. Lecturers and students will agree on lecture periods and days convenient, provided the required credit units (CU) are met.</span>
                </div>
              </div>

              <div className="space-y-3.5">
                <div>
                  <strong className="text-[#0B2644] font-sc block">4. Modular Intensive Residencies:</strong>
                  <span>Two one-week intensive residencies at the Campus are scheduled mid-semester (dates to be announced) for integrative case-study seminars, library and database research, and joint inter-cohort symposiums.</span>
                </div>

                <div>
                  <strong className="text-[#0B2644] font-sc block">5. Saturday Executive Luncheon:</strong>
                  <span>A one-hour lunch break (1:00 – 2:00 pm) is observed each Saturday for doctoral cohorts; executive refreshments are provided by the Business School.</span>
                </div>

                <div>
                  <strong className="text-[#0B2644] font-sc block">6. Course Communications & WhatsApp Groups:</strong>
                  <span>Dedicated WhatsApp groups for each course in the programme are created to ensure real-time convenience of teaching, lecture links distribution, and student-faculty interactions.</span>
                </div>
              </div>

            </div>

          </div>

          {/* ========================================================= */}
          {/* OFFICIAL INSTITUTIONAL SIGN-OFF (Name & Title Only) */}
          {/* ========================================================= */}
          <div className="mt-12 pt-8 border-t-2 border-slate-300 flex flex-col sm:flex-row items-start sm:items-end justify-between gap-6 print:mt-6 print:pt-4">
            
            {/* Accreditation Endorsement */}
            <div className="space-y-1.5 text-xs text-slate-500">
              <div className="flex items-center space-x-2 text-[#0B2644] font-bold font-sc">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <span>Ratified by Directorate Academic Board & Faculty Committee</span>
              </div>
              <p>Akwa Ibom State University Business School · NUC Accredited</p>
              <p className="font-mono text-[11px] text-slate-400">Published: 29 October 2026 · Official Circulation</p>
            </div>

            {/* Directorate Sign-Off Box with Director Photo Headshot */}
            <div className="bg-slate-50 border-2 border-slate-200 rounded-2xl p-5 sm:p-6 text-left min-w-[320px] max-w-md shadow-2xs">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#7A470E] font-bold mb-3">
                Authorised by:
              </div>
              
              <div className="flex items-center space-x-4">
                <div className="relative shrink-0">
                  <img 
                    src="/director-uwah.png" 
                    alt="Dr. Uwem E. Uwah, FCA — Director, AKSU Business School" 
                    className="w-16 h-16 sm:w-18 sm:h-18 rounded-2xl object-cover object-top border-2 border-[#7A470E] shadow-md bg-amber-50"
                  />
                  <span className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-emerald-500 border-2 border-white shadow-xs" title="Verified Director Signature Clearance" />
                </div>

                <div>
                  {/* Director's Name in Bold Imperial SC */}
                  <div className="text-base sm:text-lg font-black text-[#0B2644] font-sc tracking-wide leading-tight">
                    Dr. Uwem E. Uwah, FCA
                  </div>
                  
                  <div className="text-xs font-bold text-[#7A470E] font-sc mt-0.5">
                    Director, AKSU Business School
                  </div>
                  
                  <div className="text-[11px] text-slate-600 mt-0.5 leading-snug">
                    Akwa Ibom State University, Nigeria
                  </div>
                </div>
              </div>

              <div className="mt-3.5 pt-2.5 border-t border-slate-200 flex items-center justify-between text-[11px] font-mono text-slate-500">
                <span>Date: 29 October 2026</span>
                <span className="text-emerald-700 font-bold bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200 text-[10px]">
                  ● Official Seal
                </span>
              </div>
            </div>

          </div>

        </div>

      </div>
    </div>
  );
};
